import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * Card — surface primitive.
 *
 * Variants:
 *   - default   — elevated white surface with subtle shadow (app)
 *   - flat      — bordered, no shadow (data-density)
 *   - raised    — heavier shadow (hero feature tiles)
 *   - inverted  — dark teal surface (footer / hero overlays)
 *
 * Padding default: 6 (24px). Use `<Card.Body>` / `<Card.Header>` for sections.
 */

type CardVariant = 'default' | 'flat' | 'raised' | 'inverted';

const variantClasses: Record<CardVariant, string> = {
  default: 'bg-surface-elevated border border-border-subtle shadow-sm',
  flat: 'bg-surface-elevated border border-border-subtle',
  raised: 'bg-surface-elevated border border-border-subtle shadow-lg',
  inverted: 'bg-surface-inverted text-text-onInverted border border-brand-700',
};

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  asChild?: boolean;
  interactive?: boolean;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', interactive, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-lg',
        variantClasses[variant],
        interactive &&
          'transition-all duration-fast ease-out hover:-translate-y-0.5 hover:shadow-md cursor-pointer',
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-6 pt-6 pb-3 flex flex-col gap-1.5', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-lg font-semibold text-text-primary leading-snug', className)}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-text-secondary', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export const CardBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-6 py-4', className)} {...props} />
  ),
);
CardBody.displayName = 'CardBody';

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'px-6 py-4 border-t border-border-subtle flex items-center gap-3',
        className,
      )}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';
